import { useState } from "react";
import { createDocumentFromUrl, type SubmitUrlResult } from "../../../../lib/corpusApi";

/** A submission of fewer than this many URLs runs interactively — the backend extracts each one
 *  inline in an edge background task instead of spawning an async Tangrowth job, skipping the
 *  Modal scheduling tail for the small, human-attended batches this dialog is used for. A larger
 *  batch keeps the async spawn path, which scales out rather than tying up edge workers. */
export const INTERACTIVE_BATCH_MAX = 10;

/** Whether a batch of `urlCount` URLs should be submitted interactively (see
 *  INTERACTIVE_BATCH_MAX). Pure so the threshold is unit-testable. */
export function shouldSubmitInteractively(urlCount: number): boolean {
  return urlCount < INTERACTIVE_BATCH_MAX;
}

export interface SubmissionProgress {
  total: number;
  completed: number;
  done: boolean;
}

export interface UseSubmitUrlsResult {
  urlInput: string;
  setUrlInput: (v: string) => void;
  progress: SubmissionProgress | null;
  results: SubmitUrlResult[];
  handleSubmit: (e: React.FormEvent) => Promise<void>;
}

/**
 * Submits one or more URLs concurrently (one Edge Function request per URL, each getting its
 * own CPU budget) — this hook only covers the submit call itself, which returns fast either way
 * (a small batch extracts inline in a background task; a large one spawns an async Tangrowth job
 * without waiting on it — see shouldSubmitInteractively). Submission alone is enough: POST
 * /corpus/documents already durably records the job in extraction_jobs, and the server finishes
 * it with no further action needed here. A spawned Tangrowth job can sit "pending" for 30+
 * minutes (Modal free-tier scheduling delay), so nothing waits on it in-dialog.
 */
export function useSubmitUrls(): UseSubmitUrlsResult {
  const [urlInput, setUrlInput] = useState("");
  const [progress, setProgress] = useState<SubmissionProgress | null>(null);
  const [results, setResults] = useState<SubmitUrlResult[]>([]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const urls = urlInput
      .split("\n")
      .map((u) => u.trim())
      .filter(Boolean);
    if (urls.length === 0) return;

    const interactive = shouldSubmitInteractively(urls.length);
    setResults(urls.map((url): SubmitUrlResult => ({ url, state: "submitting" })));
    setProgress({ total: urls.length, completed: 0, done: false });

    let completedCount = 0;
    const updateResult = (index: number, result: SubmitUrlResult) => {
      setResults((prev) => prev.map((r, i) => (i === index ? result : r)));
    };
    const markSettled = () => {
      completedCount += 1;
      setProgress({
        total: urls.length,
        completed: completedCount,
        done: completedCount === urls.length,
      });
    };

    await Promise.all(
      urls.map(async (url, index) => {
        try {
          const created = await createDocumentFromUrl(url, interactive);
          switch (created.kind) {
            case "spawned":
              updateResult(index, {
                url,
                state: "submitted",
                id: created.id,
                jobId: created.jobId,
              });
              break;
            case "duplicate":
              updateResult(index, { url, state: "duplicate", id: created.id });
              break;
            case "rejected":
              updateResult(index, { url, state: "rejected", rejection: created.rejection });
              break;
          }
        } catch (err) {
          updateResult(index, {
            url,
            state: "error",
            error: err instanceof Error ? err.message : "Request failed",
          });
        }
        markSettled();
      })
    );
  }

  return { urlInput, setUrlInput, progress, results, handleSubmit };
}
